// pages/master-data/driver/MasterDriverPage.jsx
'use client';

import { useState, useEffect, useMemo, useCallback } from 'react';
import DataTable from '../../../components/DataTable';
import { Button, Chip } from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import { useDriverList, deleteDriver } from '../../../stores/driverStore';
import { useCompanyList } from '../../../stores/companyStore';
import ActionMenu from '../../../components/ActionMenu';
import { useNotifier } from '../../../components/Notifier';
import DeleteConfirmDialog from '../../../components/DeleteConfirmDialog';
import DetailDialog from '../../../components/DetailDialog';
import { fleetApi } from '../../../api/axiosClient';
import MasterDriverAddEdit from './MasterDriverAddEdit';
import { useLanguage } from '../../../utils/i18n';

import VisibilityIcon from '@mui/icons-material/Visibility';

import ExcelJS from 'exceljs';
import dayjs from 'dayjs';
import MasterDriverDetail from './MasterDriverDetail';
import { useNavigate } from 'react-router';

const formatDateShort = (val) => {
  if (!val) return '';
  try {
    const d = dayjs(val);
    if (!d.isValid()) return String(val).slice(0, 10);
    return d.format('DD-MM-YYYY');
  } catch {
    return String(val).slice(0, 10);
  }
};

const getTzParam = () => `?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`;

function MasterDriverPageContent() {
  const { t } = useLanguage();
  const notify = useNotifier();
  const navigate = useNavigate();

  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [searchField, setSearchField] = useState('name');
  const [searchRaw, setSearchRaw] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [, setSearchExtraColumns] = useState([]);

  const [localDrivers, setLocalDrivers] = useState([]);
  const [localLoading, setLocalLoading] = useState(false);
  const [localTotal, setLocalTotal] = useState(0);

  const [openDetail, setOpenDetail] = useState(false);
  const [detailRow, setDetailRow] = useState(null);

  const searchFieldOptions = useMemo(
    () => [
      { value: 'all', label: t('general.searchAll', 'All') },
      { value: 'name', label: t('masterDriver.driverName', 'Driver Name') },
      { value: 'driver_code', label: t('masterDriver.code', 'Driver Code') },
      { value: 'ktp_no', label: t('masterDriver.nik', 'NIK') },
      { value: 'mobile_no', label: t('masterUser.phone', 'Phone') }
    ],
    [t]
  );

  const { companyOptions } = useCompanyList();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTargetId, setConfirmTargetId] = useState(null);
  const [confirmName, setConfirmName] = useState('');
  const [confirmLoading, setConfirmLoading] = useState(false);

  const [selectedCompanies, setSelectedCompanies] = useState(() => {
    const stored = typeof window !== 'undefined' ? localStorage.getItem('companyId') : null;
    return stored ? [{ value: stored, label: stored }] : [];
  });

  useEffect(() => {
    const stored = typeof window !== 'undefined' ? localStorage.getItem('companyId') : null;
    if (!stored) return;
    if (companyOptions && companyOptions.length) {
      const match = companyOptions.find((c) => String(c.value) === String(stored));
      if (match) {
        setSelectedCompanies([{ value: String(match.value), label: match.label || match.name || String(match.value) }]);
      } else {
        setSelectedCompanies([{ value: String(stored), label: String(stored) }]);
      }
    }
  }, [companyOptions]);

  const companyFilterColumns = useMemo(() => {
    const storedCompanyId = typeof window !== 'undefined' ? localStorage.getItem('companyId') : null;
    if (selectedCompanies && selectedCompanies.length > 0) {
      const hasAll = selectedCompanies.some((s) => String(s.value) === 'all');
      if (hasAll) return [];
      const values = selectedCompanies.map((s) => String(s.value));
      if (values.length === 1) {
        return [{ name: 'company_id', logic_operator: '=', operator: 'AND', value: values[0] }];
      } else {
        return [{ name: 'company_id', logic_operator: 'in', operator: 'AND', value: values.join(',') }];
      }
    }
    if (storedCompanyId && String(storedCompanyId) !== 'all') {
      return [{ name: 'company_id', logic_operator: '=', operator: 'AND', value: String(storedCompanyId) }];
    }
    return [];
  }, [selectedCompanies]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const v = String(searchRaw ?? '').trim();
      if (!v || searchField === 'all') {
        setSearchExtraColumns([...companyFilterColumns]);
        setSearchValue(v);
        setPaginationModel((p) => ({ ...p, page: 0 }));
        return;
      }

      const selectedOpt = searchFieldOptions.find((o) => o.value === searchField);

      const searchCol = {
        name: selectedOpt?.fieldName || searchField,
        logic_operator: 'like',
        operator: 'AND',
        value: v
      };

      if (selectedOpt?.tableName) {
        searchCol.table_name = selectedOpt.tableName;
      }

      setSearchExtraColumns([...companyFilterColumns, searchCol]);
      setSearchValue(v);
      setPaginationModel((p) => ({ ...p, page: 0 }));
    }, 700);

    return () => clearTimeout(timer);
  }, [searchRaw, searchField, companyFilterColumns, searchFieldOptions]);

  const hookSearchField = searchField === 'all' ? '' : searchField;
  const hookSearchValue = searchField === 'all' ? '' : searchValue;

  const {
    data: driversFromHook,
    isLoading: hookLoading,
    totalRecord: hookTotal
  } = useDriverList(paginationModel.page, paginationModel.pageSize, selectedCompanies, hookSearchField, hookSearchValue);

  const rows = searchField === 'all' ? localDrivers : driversFromHook || [];
  const isLoading = searchField === 'all' ? localLoading : hookLoading;
  const totalRecords = searchField === 'all' ? localTotal : hookTotal || 0;

  useEffect(() => {
    if (searchField !== 'all') return;
    const controller = new AbortController();

    const fetchAllSearch = async () => {
      setLocalLoading(true);
      try {
        const start = paginationModel.page * paginationModel.pageSize;
        const length = paginationModel.pageSize;

        const columns = [...companyFilterColumns];
        if (columns.length === 0) {
          columns.push({ logic_operator: 'like', name: 'name', operator: 'AND', value: '' });
        }

        let filters = [];
        if (searchValue) {
          filters = [
            { name: 'name', value: searchValue },
            { name: 'driver_code', value: searchValue },
            { name: 'ktp_no', value: searchValue },
            { name: 'mobile_no', value: searchValue }
          ];
        }

        const payload = {
          paging: { start, length },
          orders: { columns: ['name'], ascending: true },
          joins: [
            { name: 'company', column_results: ['name'] },
            { name: 'vehicle', column_results: ['license_plate'] },
            { name: 'driver_license', column_results: ['license_name'] }
          ],
          columns: columns,
          filters: filters
        };

        const resp = await fleetApi.post('/driver/search' + getTzParam(), payload, { signal: controller.signal });
        const data = resp?.data ?? null;

        const fetchedRows = Array.isArray(data) ? data : (data?.data ?? []);
        const total = Array.isArray(data) ? fetchedRows.length : (data?.totalRecord ?? data?.totalRecords ?? data?.dataSize ?? 0);

        setLocalDrivers(fetchedRows);
        setLocalTotal(total);
      } catch (err) {
        if (err.name !== 'AbortError') console.error('Driver search all error', err);
      } finally {
        if (!controller.signal.aborted) setLocalLoading(false);
      }
    };

    fetchAllSearch();
    return () => controller.abort();
  }, [searchField, searchValue, paginationModel, companyFilterColumns]);

  const handleFetchAllForExport = async () => {
    try {
      const length = totalRecords > 0 ? totalRecords : 9999;
      let filters = [];
      let exportColumns = [...companyFilterColumns];

      if (searchValue) {
        if (searchField === 'all') {
          filters = [
            { name: 'name', value: searchValue },
            { name: 'driver_code', value: searchValue },
            { name: 'ktp_no', value: searchValue },
            { name: 'mobile_no', value: searchValue }
          ];
        } else {
          const selectedOpt = searchFieldOptions.find((o) => o.value === searchField);
          const searchCol = {
            name: selectedOpt?.fieldName || searchField,
            logic_operator: 'like',
            operator: 'AND',
            value: searchValue
          };
          if (selectedOpt?.tableName) searchCol.table_name = selectedOpt.tableName;
          exportColumns.push(searchCol);
        }
      }

      if (exportColumns.length === 0) {
        exportColumns.push({ logic_operator: 'like', name: 'name', operator: 'AND', value: '' });
      }

      const payload = {
        paging: { start: 0, length },
        orders: { columns: ['name'], ascending: true },
        joins: [
          { name: 'company', column_results: ['name'] },
          { name: 'vehicle', column_results: ['license_plate'] },
          { name: 'driver_license', column_results: ['license_name'] }
        ],
        columns: exportColumns,
        filters: filters
      };

      const resp = await fleetApi.post('/driver/search' + getTzParam(), payload);
      const allData = Array.isArray(resp?.data) ? resp.data : resp?.data?.data || [];
      return allData;
    } catch (err) {
      console.error('Driver export fetch all error', err);
      return [];
    }
  };

  // --- 6. HANDLERS ---
  const handlePageChange = (model) => setPaginationModel(model);

  const handleAddClick = () => {
    setEditingId(null);
    setOpenDialog(true);
  };

  const handleEditClick = (id) => {
    setEditingId(id);
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setEditingId(null);
  };

  const handleSearch = ({ field, value }) => {
    const safeField = typeof field === 'object' ? field?.value : field;
    setSearchField(safeField || 'all');
    setSearchRaw(value ?? '');
    setPaginationModel((p) => ({ ...p, page: 0 }));
  };

  const handleCompanyChange = (options) => {
    setSelectedCompanies(options);
    try {
      if (typeof window !== 'undefined') {
        if (options.length === 1 && options[0].value !== 'all') {
          localStorage.setItem('companyId', String(options[0].value));
        } else {
          localStorage.removeItem('companyId');
        }
      }
    } catch {
      // ignore
    }
  };

  const handleDelete = (id, itemName) => {
    setConfirmTargetId(id);
    setConfirmName(itemName || '');
    setConfirmOpen(true);
  };

  const handleConfirmClose = () => {
    if (confirmLoading) return;
    setConfirmOpen(false);
    setConfirmTargetId(null);
    setConfirmName('');
  };

  const handleConfirmDelete = async () => {
    if (!confirmTargetId) return;
    setConfirmLoading(true);
    try {
      await deleteDriver(confirmTargetId);
      notify({ message: t('notify.deleted', 'Deleted successfully'), severity: 'success' });
      setPaginationModel((p) => ({ ...p }));
    } catch (err) {
      console.error(err);
      notify({ message: t('notify.deleteFailed', 'Failed to delete'), severity: 'error' });
    } finally {
      setConfirmLoading(false);
      setConfirmOpen(false);
      setConfirmTargetId(null);
      setConfirmName('');
    }
  };

  const handleDriverDetail = (row) => {
    if (!row) {
      notify({ message: t('masterDriver.message1', 'Driver data not found'), severity: 'error' });
      return;
    }
    setDetailRow(row);
    setOpenDetail(true);
  };

  const handleShowSetDriver = useCallback(() => {
    navigate(`/settings-driver?name=settings-driver`);
  }, [navigate]);

  // --- 7. EXPORT EXCEL LOGIC ---
  const EXPORT_COLUMNS = [
    { field: 'name', headerName: t('masterDriver.name', 'Nama') },
    { field: 'driver_type', headerName: t('masterDriver.position', 'Jabatan') },
    { field: 'incharge', headerName: t('masterDriver.inCharge', 'In Charge') },
    { field: 'driver_code', headerName: t('masterDriver.nik', 'NIK') },
    { field: 'mobile_no', headerName: t('masterDriver.mobileNo', 'No. Handphone') },
    { field: 'license_type', headerName: t('masterDriver.driverLicenseType', 'Jenis SIM Driver') },
    { field: 'license_no', headerName: t('masterDriver.driverLicenseNo', 'No. SIM Driver') },
    { field: 'license_exp_date', headerName: t('masterDriver.driverLicenseExp', 'Exp. SIM Driver') },
    { field: 'internal_license_no', headerName: t('masterDriver.internalLicenseNo', 'No. SIM Internal') },
    { field: 'internal_license_exp_date', headerName: t('masterDriver.internalLicenseExp', 'Exp. SIM Internal') },
    { field: 'sio_internal_no', headerName: t('masterDriver.internalSioNo', 'No. SIO Internal') },
    { field: 'sio_internal_exp_date', headerName: t('masterDriver.internalSioExp', 'Exp. SIO Internal') },
    { field: 'sio_disnaker_no', headerName: t('masterDriver.disnakerSioNo', 'No. SIO Disnaker') },
    { field: 'sio_disnaker_exp_date', headerName: t('masterDriver.disnakerSioExp', 'Exp. SIO Disnaker') },
    { field: 'ktp_no', headerName: t('masterDriver.eKtpNo', 'No. E-KTP') },
    { field: 'birth_info', headerName: t('masterDriver.birthInfo', 'Tempat, Tanggal Lahir') },
    { field: 'latest_education', headerName: t('masterDriver.education', 'Pendidikan') },
    { field: 'address', headerName: t('masterDriver.address', 'Alamat') },
    { field: 'kk_no', headerName: t('masterDriver.kkNo', 'No. Kartu Keluarga') },
    { field: 'bpjs_kesehatan', headerName: t('masterDriver.bpjsKesehatan', 'BPJS Kesehatan') },
    { field: 'bpjs_tenagakerja', headerName: t('masterDriver.bpjsKetenagakerjaan', 'BPJS Ketenagakerjaan') },
    { field: 'npwp', headerName: t('masterDriver.npwp', 'NPWP') }
  ];

  const handleExportDetailedExcel = async () => {
    notify({ message: t('masterDriver.exportGenerating', 'Generating Excel file, please wait...'), severity: 'info' });
    try {
      let filters = [];
      let exportColumns = [...companyFilterColumns];

      if (searchRaw) {
        if (searchField === 'all') {
          // Mode Global Search Export (SAMA SEPERTI DATATABLE)
          filters = [
            { name: 'name', value: searchRaw },
            { name: 'driver_code', value: searchRaw },
            { name: 'ktp_no', value: searchRaw },
            { name: 'mobile_no', value: searchRaw }
          ];
        } else {
          // Mode Specific Column Export
          const selectedOpt = searchFieldOptions.find((o) => o.value === searchField);
          const searchCol = {
            name: selectedOpt?.fieldName || searchField,
            logic_operator: 'like',
            operator: 'AND',
            value: searchRaw
          };
          if (selectedOpt?.tableName) {
            searchCol.table_name = selectedOpt.tableName;
          }
          exportColumns.push(searchCol);
        }
      }

      // Minimal payload fallback untuk array columns
      if (exportColumns.length === 0) {
        exportColumns.push({ logic_operator: 'like', name: 'name', operator: 'AND', value: '' });
      }

      const payload = {
        paging: { start: 0, length: -1 },
        orders: { columns: ['name'], ascending: true },
        joins: [
          { name: 'company', column_results: ['name'] },
          { name: 'vehicle', column_results: ['license_plate'] },
          { name: 'driver_license', column_results: ['license_name'] }
        ],
        columns: exportColumns,
        filters: filters
      };

      const resp = await fleetApi.post('/driver/search' + getTzParam(), payload);
      const allData = Array.isArray(resp.data) ? resp.data : resp.data?.data || [];

      if (allData.length === 0) {
        notify({ message: t('masterDriver.exportNoData', 'No data to export'), severity: 'warning' });
        return;
      }

      const currentUserRole =
        typeof window !== 'undefined' ? localStorage.getItem('rolename') || localStorage.getItem('role_name') || '' : '';
      const finalExportCols = [...EXPORT_COLUMNS];
      if (currentUserRole === 'Administrator') {
        finalExportCols.push(
          { field: 'created_by', headerName: t('masterDriver.createdBy', 'Created By') },
          { field: 'created_date', headerName: t('masterDriver.createdDate', 'Created Date') },
          { field: 'modified_by', headerName: t('masterDriver.modifiedBy', 'Modified By') },
          { field: 'modified_date', headerName: t('masterDriver.modifiedDate', 'Modified Date') }
        );
      }

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(t('masterDriver.exportTitle', 'Driver Report'));

      worksheet.columns = finalExportCols.map((col) => ({
        header: col.headerName,
        key: col.field
      }));

      allData.forEach((item) => {
        const getLic = (name) => item.driver_license?.find((lic) => lic.join?.driver_license_license_name === name);
        const simInternal = getLic('SIM Internal');
        const sioDisnaker = getLic('SIO Disnaker');
        const sioInternal = getLic('SIO Internal');
        const ktpLic = getLic('KTP');
        const kkLic = getLic('KK');
        const bpjsKes = getLic('BPJS KESEHATAN');
        const bpjsTk = getLic('BPJS TENAGA KERJA');
        const npwpLic = getLic('NPWP');

        const simDriver = getLic('SIM') || item.driver_license?.[0];
        const bDate = item.birth_date || '-';
        const birthInfo = `${item.birth_place || ''}, ${bDate}`;

        const rowData = {
          name: item.name || '-',
          driver_type: item.driver_type || '-',
          incharge: formatDateShort(item.hired_date) || '-',
          driver_code: item.driver_code || '-',
          phone: item.mobile_no || '-',
          license_type: simDriver?.join?.driver_license_license_name || '-',
          license_no: simDriver?.license_no || '-',
          license_exp_date: formatDateShort(simDriver?.license_exp_date) || '-',
          internal_license_no: simInternal?.license_no || '-',
          internal_license_exp_date: formatDateShort(simInternal?.license_exp_date) || '-',
          sio_internal_no: sioInternal?.license_no || '-',
          sio_internal_exp_date: formatDateShort(sioInternal?.license_exp_date) || '-',
          sio_disnaker_no: sioDisnaker?.license_no || '-',
          sio_disnaker_exp_date: formatDateShort(sioDisnaker?.license_exp_date) || '-',
          ktp_no: ktpLic?.license_no || item.ktp_no || '-',
          birth_info: birthInfo,
          latest_education: item.latest_education || '-',
          address: item.address || '-',
          kk_no: kkLic?.license_no || '-',
          bpjs_kesehatan: bpjsKes?.license_no || '-',
          bpjs_tenagakerja: bpjsTk?.license_no || '-',
          npwp: npwpLic?.license_no || '-'
        };

        if (currentUserRole === 'Administrator') {
          rowData.created_by = item.created_by || '-';
          rowData.created_date = item.created_date || '-';
          rowData.modified_by = item.modified_by || '-';
          rowData.modified_date = item.modified_date || '-';
        }

        worksheet.addRow(rowData);
      });

      worksheet.columns.forEach((column) => {
        let maxColumnLength = 0;
        column.eachCell({ includeEmpty: true }, (cell) => {
          const cellValue = cell.value ? cell.value.toString() : '';
          maxColumnLength = Math.max(maxColumnLength, cellValue.length);
        });
        column.width = maxColumnLength < 10 ? 10 : maxColumnLength + 2;
      });

      worksheet.getRow(1).font = { bold: true };
      worksheet.getRow(1).alignment = { vertical: 'middle', horizontal: 'center' };

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `Export_Driver_Detailed.xlsx`;
      link.click();

      notify({ message: t('masterDriver.exportSuccess', 'Export successful'), severity: 'success' });
    } catch (err) {
      console.error('Export Error:', err);
      notify({ message: t('masterDriver.exportFailed', 'Failed to export data'), severity: 'error' });
    }
  };

  // --- 8. RENDER COLUMNS ---
  const getCompanyLabel = (row) => {
    if (!row) return '-';
    if (row?.join?.company_name) return row.join.company_name;
    const companyId = row?.company ?? row?.company_id ?? null;
    if (companyId && companyOptions) {
      const found = companyOptions.find((c) => Number(c.value) === Number(companyId));
      if (found) return found.label;
    }
    return '-';
  };
  const currentUserRole = typeof window !== 'undefined' ? localStorage.getItem('rolename') || localStorage.getItem('role_name') || '' : '';

  const columns = [
    { field: 'company', headerName: t('general.company', 'Company'), flex: 1, renderCell: (params) => getCompanyLabel(params.row) },
    { field: 'driver_code', headerName: t('masterDriver.code', 'Driver Code'), flex: 1 },
    { field: 'name', headerName: t('masterDriver.driverName', 'Driver Name'), flex: 1 },
    { field: 'ktp_no', headerName: t('masterDriver.nik', 'NIK'), flex: 1 },
    { field: 'mobile_no', headerName: t('masterUser.phone', 'Phone'), flex: 1 },
    { field: 'email', headerName: t('masterDriver.email', 'Email'), hide: true },
    { field: 'driver_type', headerName: t('masterDriver.driverType', 'Driver Type'), hide: true },
    { field: 'birth_date', headerName: t('masterDriver.birthDate', 'Birth Date'), hide: true },
    { field: 'birth_place', headerName: t('masterDriver.birthPlace', 'Birth Place'), hide: true },
    { field: 'hired_date', headerName: t('masterDriver.hiredDate', 'Hired Date'), hide: true },
    {
      field: 'driver_license_license_name',
      headerName: 'License Type',
      hide: true,
      valueGetter: (value, row) => {
        const r = row?.row ?? row;
        const licenses = r?.driver_license || [];
        if (!Array.isArray(licenses) || licenses.length === 0) return '-';
        return licenses.map((l) => l?.join?.driver_license_license_name || '-');
      }
    },
    {
      field: 'license_exp_date',
      headerName: t('masterDriver.licenseExpDate', 'License Exp Date'),
      hide: true,
      valueGetter: (value, row) => {
        const r = row?.row ?? row;
        const licenses = r?.driver_license || [];
        if (!Array.isArray(licenses) || licenses.length === 0) return '-';
        return licenses.map((l) => (l?.license_exp_date ? l.license_exp_date.split('T')[0] : '-'));
      }
    },
    {
      field: 'license_no',
      headerName: t('masterDriver.licenseNo', 'License No'),
      hide: true,
      valueGetter: (value, row) => {
        const r = row?.row ?? row;
        const licenses = r?.driver_license || [];
        if (!Array.isArray(licenses) || licenses.length === 0) return '-';
        return licenses.map((l) => l?.license_no || '-');
      }
    },
    {
      field: 'status',
      headerName: t('masterDriver.status', 'Status'),
      headerAlign: 'center',
      align: 'center',
      flex: 1,
      valueGetter: (value, row) => {
        const st = row?.status ?? row?.is_active ?? null;
        if (st === null || st === undefined) return '-';
        if (typeof st === 'boolean') return st ? 'Active' : 'Not Active';
        return String(st);
      },
      renderCell: (params) => {
        const st = params.row?.status ?? params.row?.is_active ?? null;
        if (st === null || st === undefined) return '-';
        if (typeof st === 'boolean') {
          return st ? (
            <Chip label={t('general.active', 'Active')} color="success" variant="outlined" size="small" />
          ) : (
            <Chip label={t('general.notActive', 'Not Active')} color="error" variant="outlined" size="small" />
          );
        }
        return <Chip label={String(st)} variant="outlined" size="small" />;
      }
    },

    ...(currentUserRole === 'Administrator'
      ? [
          { field: 'created_by', headerName: t('masterDriver.createdBy', 'Created By'), flex: 0.5, hide: true },
          { field: 'created_date', headerName: t('masterDriver.createdDate', 'Created Date'), flex: 0.5, hide: true },
          { field: 'modified_by', headerName: t('masterDriver.modifiedBy', 'Modified By'), flex: 0.5, hide: true },
          { field: 'modified_date', headerName: t('masterDriver.modifiedDate', 'Modified Date'), flex: 0.5, hide: true }
        ]
      : []),
    {
      field: 'actions',
      headerName: t('general.actions', 'Actions'),
      sortable: false,
      flex: 1,
      headerAlign: 'center',
      renderCell: (params) => (
        <ActionMenu
          row={params.row}
          onEdit={handleEditClick}
          onDelete={(id) => handleDelete(id, params.row.name)}
          mode="inline"
          extraActions={[
            {
              label: t('masterVehicle.vehicleDetail', 'Vehicle Detail'),
              icon: <VisibilityIcon fontSize="small" />,
              onClick: (row) => handleDriverDetail(row)
            }
          ]}
        />
      )
    }
  ];

  return (
    <div>
      <DataTable
        title={t('masterDriver.master', 'Master Driver')}
        onFetchAllForExport={handleFetchAllForExport}
        rows={rows}
        columns={columns}
        loading={isLoading}
        pageSizeOptions={[10, 25, 50]}
        paginationMode="server"
        rowCount={totalRecords}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePageChange}
        searchQuery={searchValue}
        onSearch={({ field, value }) => handleSearch({ field, value })}
        searchFieldOptions={searchFieldOptions}
        selectedSearchField={searchField}
        onSearchFieldChange={(f) => {
          const safeField = typeof f === 'object' ? f?.value : f;
          setSearchField(safeField || 'all');
          setSearchRaw('');
          setSearchValue('');
          setSearchExtraColumns([...companyFilterColumns]);
          setPaginationModel((p) => ({ ...p, page: 0 }));
        }}
        onAddClick={handleAddClick}
        companyOptions={companyOptions}
        selectedCompanies={selectedCompanies}
        onCompanyChange={handleCompanyChange}
        onExportExcel={handleExportDetailedExcel}
        extraButtons={
          <Button variant="contained" color="info" startIcon={<TuneIcon />} onClick={handleShowSetDriver}>
            {t('masterDriver.setDriver', 'Set Driver')}
          </Button>
        }
      />

      <MasterDriverAddEdit
        open={openDialog}
        id={editingId}
        companyOptions={companyOptions}
        onClose={handleDialogClose}
        onSuccess={(msg) => {
          setPaginationModel((p) => ({ ...p }));
        }}
        onError={(msg) => notify({ message: msg ?? t('notify.failedSave', 'Failed to save'), severity: 'error' })}
      />

      <DetailDialog
        open={openDetail}
        onClose={() => {
          setOpenDetail(false);
          setDetailRow(null);
        }}
        title={t('masterDriver.detailDriver', 'Driver Details')}
        fullHeight={false}
        fullScreenMode={true}
      >
        <MasterDriverDetail
          driver={detailRow}
          companyOptions={companyOptions}
          onClose={() => {
            setOpenDetail(false);
            setDetailRow(null);
          }}
        />
      </DetailDialog>

      <DeleteConfirmDialog
        open={confirmOpen}
        loading={confirmLoading}
        onClose={handleConfirmClose}
        onConfirm={handleConfirmDelete}
        title={t('deleteConfirm.title', 'Confirm Delete')}
        message={t('deleteConfirm.message', 'Are you sure you want to delete this item?')}
        confirmLabel={t('deleteConfirm.label', 'Delete')}
        cancelLabel={t('deleteConfirm.cancel', 'Cancel')}
        itemName={confirmName}
      />
    </div>
  );
}

export default function MasterDriverPage() {
  return (
    <>
      <MasterDriverPageContent />
    </>
  );
}
